import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Save, ArrowLeft, Plus, X, ShieldCheck, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPriceFull } from "@/lib/format";
import { PhotosEditor } from "@/components/admin/PhotosEditor";

const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid", "Other"];
const TRANS = ["Manual", "Automatic", "AMT", "CVT", "DCT", "Other"];
const BODIES = ["Hatchback", "Sedan", "SUV", "MUV", "Coupe", "Convertible", "Luxury", "Other"];
const STATUSES = ["available", "reserved", "sold", "draft", "inactive", "archived"];

const EMPTY = {
  make: "", model: "", variant: "", year: new Date().getFullYear() - 2, km_driven: 0,
  fuel: "Petrol", transmission: "Manual", body_type: "Hatchback", owners: 1, price: 0,
  original_price: null, negotiable: true, finance_available: true, color: "",
  registration_state: "DL", registration_number: "", registration_year: null,
  insurance: "", insurance_valid_till: "", mileage: "", engine: "",
  short_description: "", description: "", highlights: [], features: [],
  exterior_condition: "", interior_condition: "", tyres_condition: "", engine_condition: "", mechanical_notes: "",
  images: [], primary_image_idx: 0, documents: [],
  cs_certified: false, inspection: {}, inspection_date: "", inspection_score: null, inspection_notes: "",
  service_history: "", status: "draft", location: "Vikaspuri, Delhi",
  show_on_website: true, featured: false, featured_order: 0, badges: [],
  seo_title: "", seo_description: "",
  purchase_date: new Date().toISOString().slice(0, 10), purchase_price: null,
  additional_expenditure: 0, bank_commission: 0, refundable_security: 0, other_costs: 0,
};

export default function VehicleFormPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = !id || id === "new";
  const [v, setV] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [tab, setTab] = useState("basic");

  useEffect(() => {
    if (!isNew) { api.get(`/vehicles/${id}`).then((r) => setV({ ...EMPTY, ...r.data })).finally(() => setLoading(false)); }
  }, [id, isNew]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  const save = async (status) => {
    try {
      const payload = { ...v };
      if (status) payload.status = status;
      if (!payload.make || !payload.model || !payload.price) { toast.error("Make, Model and Price are required"); return; }
      if (isNew) {
        const { data } = await api.post("/vehicles", payload);
        toast.success("Vehicle created"); nav(`/admin/inventory/${data.id}`);
      } else {
        await api.patch(`/vehicles/${id}`, payload);
        toast.success("Saved");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const costBasis = (v.purchase_price || 0) + (v.additional_expenditure || 0) + (v.bank_commission || 0) + (v.other_costs || 0);
  const margin = (v.price || 0) - costBasis;
  const marginPct = v.price > 0 ? (margin / v.price) * 100 : 0;

  if (loading) return <div className="text-gray-500">Loading…</div>;

  const tabs = [
    { id: "basic", label: "Basic & Specs" },
    { id: "pricing", label: "Pricing & Finance" },
    { id: "photos", label: "Photos & Documents" },
    { id: "desc", label: "Description" },
    { id: "inspection", label: "Inspection" },
    { id: "publish", label: "Publish & SEO" },
    ...(!isNew ? [{ id: "activity", label: "Activity" }] : []),
  ];

  return (
    <div className="space-y-5" data-testid="admin-vehicle-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/inventory" className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#940C2C]"><ArrowLeft size={12} /> Inventory</Link>
          <h1 className="mt-1 font-display font-extrabold text-2xl text-gray-900">{isNew ? "Add Vehicle" : `Edit · ${v.stock_id || ""}`}</h1>
          {!isNew && <p className="text-sm text-gray-500">{v.year} {v.make} {v.model} {v.variant}</p>}
        </div>
        <div className="flex gap-2">
          {!isNew && <a href={`/car/${id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5"><Eye size={14} /> Preview</a>}
          <button onClick={() => save("draft")} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold">Save Draft</button>
          <button onClick={() => save("available")} className="btn-primary" data-testid="admin-save-vehicle-btn"><Save size={14} /> {isNew ? "Save & Publish" : "Save"}</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex overflow-x-auto border-b border-gray-100 no-scrollbar">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-5 py-3 text-sm font-semibold whitespace-nowrap ${tab === t.id ? "text-[#940C2C] border-b-2 border-[#940C2C]" : "text-gray-500 hover:text-gray-900"}`}>{t.label}</button>
          ))}
        </div>

        <div className="p-6">
          {tab === "basic" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Stock ID (auto if blank)" value={v.stock_id} onChange={(x) => set("stock_id", x)} />
              <F label="Make *" value={v.make} onChange={(x) => set("make", x)} />
              <F label="Model *" value={v.model} onChange={(x) => set("model", x)} />
              <F label="Variant" value={v.variant} onChange={(x) => set("variant", x)} />
              <F label="Year *" type="number" value={v.year} onChange={(x) => set("year", Number(x))} />
              <F label="Registration Year" type="number" value={v.registration_year || ""} onChange={(x) => set("registration_year", x ? Number(x) : null)} />
              <F label="Registration Number" value={v.registration_number} onChange={(x) => set("registration_number", x.toUpperCase())} />
              <F label="Registration State" value={v.registration_state} onChange={(x) => set("registration_state", x)} />
              <F label="Owners" type="number" value={v.owners} onChange={(x) => set("owners", Number(x))} />
              <S label="Fuel *" value={v.fuel} onChange={(x) => set("fuel", x)} options={FUELS} />
              <S label="Transmission *" value={v.transmission} onChange={(x) => set("transmission", x)} options={TRANS} />
              <S label="Body Type *" value={v.body_type} onChange={(x) => set("body_type", x)} options={BODIES} />
              <F label="KM Driven *" type="number" value={v.km_driven} onChange={(x) => set("km_driven", Number(x))} />
              <F label="Colour" value={v.color} onChange={(x) => set("color", x)} />
              <F label="Mileage" value={v.mileage} onChange={(x) => set("mileage", x)} placeholder="e.g. 18.5 kmpl" />
              <F label="Engine" value={v.engine} onChange={(x) => set("engine", x)} placeholder="e.g. 1498 cc" />
              <F label="Insurance" value={v.insurance} onChange={(x) => set("insurance", x)} />
              <F label="Insurance Valid Till" type="date" value={v.insurance_valid_till} onChange={(x) => set("insurance_valid_till", x)} />
            </div>
          )}

          {tab === "pricing" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <F label="Selling Price (₹) *" type="number" value={v.price} onChange={(x) => set("price", Number(x))} />
              <F label="Original/List Price (₹)" type="number" value={v.original_price || ""} onChange={(x) => set("original_price", x ? Number(x) : null)} />
              <div className="flex items-end gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.negotiable} onChange={(e) => set("negotiable", e.target.checked)} className="h-4 w-4 accent-[#940C2C]" /> Negotiable</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.finance_available} onChange={(e) => set("finance_available", e.target.checked)} className="h-4 w-4 accent-[#940C2C]" /> Finance available</label>
              </div>
              <div className="md:col-span-3 mt-4 pt-4 border-t">
                <h3 className="font-display font-bold text-sm text-gray-900">Internal — NEVER shown publicly</h3>
                <p className="text-xs text-gray-500">Costs used to compute margin. Hidden from customer-facing pages.</p>
              </div>
              <F label="Purchase Date" type="date" value={v.purchase_date} onChange={(x) => set("purchase_date", x)} />
              <F label="Purchase Price (₹)" type="number" value={v.purchase_price || ""} onChange={(x) => set("purchase_price", x ? Number(x) : null)} />
              <F label="Additional Expenditure (₹)" type="number" value={v.additional_expenditure} onChange={(x) => set("additional_expenditure", Number(x))} />
              <F label="Bank Commission (₹)" type="number" value={v.bank_commission} onChange={(x) => set("bank_commission", Number(x))} />
              <F label="Other Costs (₹)" type="number" value={v.other_costs} onChange={(x) => set("other_costs", Number(x))} />
              <F label="Refundable Security (₹, not in cost basis)" type="number" value={v.refundable_security} onChange={(x) => set("refundable_security", Number(x))} />

              <div className="md:col-span-3 mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Metric label="Total Cost Basis" value={formatPriceFull(costBasis)} />
                <Metric label="Potential Gross Margin" value={formatPriceFull(margin)} accent={margin > 0} />
                <Metric label="Margin %" value={`${marginPct.toFixed(1)}%`} accent={marginPct > 10} />
              </div>
            </div>
          )}

          {tab === "photos" && (
            <PhotosEditor images={v.images} primary={v.primary_image_idx} onImages={(imgs) => set("images", imgs)} onPrimary={(i) => set("primary_image_idx", i)} documents={v.documents} onDocuments={(d) => set("documents", d)} />
          )}

          {tab === "desc" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><TA label="Short Description (1 line)" value={v.short_description} onChange={(x) => set("short_description", x)} rows={2} /></div>
              <div className="md:col-span-2"><TA label="Detailed Description" value={v.description} onChange={(x) => set("description", x)} rows={5} /></div>
              <ListField label="Highlights" items={v.highlights} onChange={(x) => set("highlights", x)} placeholder="e.g. Full service history" />
              <ListField label="Features" items={v.features} onChange={(x) => set("features", x)} placeholder="e.g. Sunroof" />
              <TA label="Exterior Condition" value={v.exterior_condition} onChange={(x) => set("exterior_condition", x)} rows={2} />
              <TA label="Interior Condition" value={v.interior_condition} onChange={(x) => set("interior_condition", x)} rows={2} />
              <TA label="Tyres Condition" value={v.tyres_condition} onChange={(x) => set("tyres_condition", x)} rows={2} />
              <TA label="Engine Condition" value={v.engine_condition} onChange={(x) => set("engine_condition", x)} rows={2} />
              <div className="md:col-span-2"><TA label="Mechanical Notes" value={v.mechanical_notes} onChange={(x) => set("mechanical_notes", x)} rows={3} /></div>
              <div className="md:col-span-2"><TA label="Service History" value={v.service_history} onChange={(x) => set("service_history", x)} rows={3} /></div>
            </div>
          )}

          {tab === "inspection" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center gap-2 text-sm md:col-span-3">
                <input type="checkbox" checked={v.cs_certified} onChange={(e) => set("cs_certified", e.target.checked)} className="h-4 w-4 accent-[#940C2C]" />
                <ShieldCheck size={14} className="text-[#940C2C]" /> <span className="font-bold">CS Certified</span>
              </label>
              <F label="Inspection Date" type="date" value={v.inspection_date} onChange={(x) => set("inspection_date", x)} />
              <F label="Inspection Score (0–100)" type="number" value={v.inspection_score || ""} onChange={(x) => set("inspection_score", x ? Number(x) : null)} />
              <div></div>
              <div className="md:col-span-3"><TA label="Overall Inspection Notes" value={v.inspection_notes} onChange={(x) => set("inspection_notes", x)} rows={3} /></div>
              <div className="md:col-span-3 mt-2">
                <h3 className="font-display font-bold text-sm text-gray-900 mb-3">Inspection Categories</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {["Engine", "Transmission", "Exterior", "Interior", "Electrical", "Tyres", "Documentation", "Ownership"].map((cat) => (
                    <TA key={cat} label={cat} value={v.inspection?.[cat] || ""} onChange={(x) => set("inspection", { ...(v.inspection || {}), [cat]: x })} rows={2} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "publish" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <S label="Status" value={v.status} onChange={(x) => set("status", x)} options={STATUSES} />
              <F label="Location" value={v.location} onChange={(x) => set("location", x)} />
              <div className="flex items-center gap-6 md:col-span-2">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.show_on_website} onChange={(e) => set("show_on_website", e.target.checked)} className="h-4 w-4 accent-[#940C2C]" /> Show on website</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#940C2C]" /> Featured on homepage</label>
                {v.featured && <F label="Featured order" type="number" value={v.featured_order} onChange={(x) => set("featured_order", Number(x))} />}
              </div>
              <ListField label="Badges" items={v.badges} onChange={(x) => set("badges", x)} placeholder="e.g. 1st Owner" />
              <div className="md:col-span-2 mt-4 pt-4 border-t">
                <h3 className="font-display font-bold text-sm text-gray-900">SEO</h3>
              </div>
              <F label="SEO Title" value={v.seo_title} onChange={(x) => set("seo_title", x)} />
              <F label="URL Slug (auto if blank)" value={v.slug || ""} onChange={(x) => set("slug", x)} />
              <div className="md:col-span-2"><TA label="SEO Description" value={v.seo_description} onChange={(x) => set("seo_description", x)} rows={3} /></div>
            </div>
          )}

          {tab === "activity" && (
            <div className="space-y-2">
              {(v.activity || []).slice().reverse().map((a, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0">
                  <div className="h-7 w-7 rounded-full bg-[#F6E6EB] text-[#940C2C] flex items-center justify-center text-[10px] font-bold">{a.action[0].toUpperCase()}</div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900">{a.action.replace(/_/g, " ")}</div>
                    <div className="text-xs text-gray-500">{a.detail}</div>
                  </div>
                  <div className="text-xs text-gray-400">{a.by} · {new Date(a.ts).toLocaleString()}</div>
                </div>
              ))}
              {(!v.activity || v.activity.length === 0) && <p className="text-sm text-gray-400">No activity yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const F = ({ label, type = "text", value, onChange, placeholder }) => (
  <div><label className="text-xs font-semibold text-gray-600">{label}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-[#940C2C]" /></div>
);
const TA = ({ label, value, onChange, rows = 3 }) => (
  <div><label className="text-xs font-semibold text-gray-600">{label}</label>
    <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-[#940C2C]" /></div>
);
const S = ({ label, value, onChange, options }) => (
  <div><label className="text-xs font-semibold text-gray-600">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm">{options.map((o) => <option key={o}>{o}</option>)}</select></div>
);
const Metric = ({ label, value, accent }) => (
  <div className={`rounded-lg p-3 border ${accent ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-gray-50"}`}>
    <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{label}</div>
    <div className={`mt-1 font-display font-extrabold text-lg ${accent ? "text-emerald-700" : "text-gray-900"}`}>{value}</div>
  </div>
);

const ListField = ({ label, items, onChange, placeholder }) => {
  const [val, setVal] = useState("");
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <div className="mt-1 flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val) { onChange([...items, val]); setVal(""); } } }} placeholder={placeholder} className="flex-1 rounded-md border border-gray-200 px-3 py-2 text-sm" />
        <button type="button" onClick={() => { if (val) { onChange([...items, val]); setVal(""); } }} className="rounded-md bg-gray-900 text-white px-3 text-sm"><Plus size={14} /></button>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-xs">{it} <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}><X size={10} /></button></span>
        ))}
      </div>
    </div>
  );
};

const PhotosEditorInline = () => null; // replaced by import; keep original PhotosEditor at bottom unused
